import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Navigation, Phone, RefreshCw, MapPin, Clock, 
  Truck, Car, AlertCircle, User, Loader2
} from 'lucide-react';

// Provider marker (blue car icon)
const providerIcon = new L.DivIcon({
  html: `
    <div style="
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: pulse 2s infinite;
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="none">
        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
      </svg>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
  className: 'provider-marker'
});

// Customer marker (red pin)
const customerIcon = new L.DivIcon({
  html: `
    <div style="
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
      border-radius: 50% 50% 50% 0;
      border: 3px solid white;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none" style="transform: rotate(45deg);">
        <circle cx="12" cy="12" r="4"/>
      </svg>
    </div>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  className: 'customer-marker'
});

// Map bounds updater component
function MapBoundsUpdater({ providerLocation, customerLocation }) {
  const map = useMap();
  
  useEffect(() => {
    if (providerLocation && customerLocation) {
      const bounds = L.latLngBounds([
        [providerLocation.lat, providerLocation.lng],
        [customerLocation.lat, customerLocation.lng]
      ]);
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
    }
  }, [providerLocation, customerLocation, map]);
  
  return null;
}

// Animated marker component for smooth movement
function AnimatedMarker({ position, icon, children }) {
  const markerRef = useRef(null);
  const prevPosition = useRef(position);
  
  useEffect(() => {
    if (markerRef.current && position) {
      const marker = markerRef.current;
      const startLat = prevPosition.current?.lat || position.lat;
      const startLng = prevPosition.current?.lng || position.lng;
      const endLat = position.lat;
      const endLng = position.lng;
      
      // Animate over 1 second
      const duration = 1000;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        const lat = startLat + (endLat - startLat) * easeProgress;
        const lng = startLng + (endLng - startLng) * easeProgress;
        
        marker.setLatLng([lat, lng]);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
      prevPosition.current = position;
    }
  }, [position]);
  
  if (!position) return null;
  
  return (
    <Marker 
      ref={markerRef}
      position={[position.lat, position.lng]} 
      icon={icon}
    >
      {children}
    </Marker>
  );
}

// Calculate ETA based on distance and average speed
function calculateETA(providerLoc, customerLoc) {
  if (!providerLoc || !customerLoc) return null;
  
  // Haversine formula for distance
  const R = 3959; // Earth's radius in miles
  const lat1 = providerLoc.lat * Math.PI / 180;
  const lat2 = customerLoc.lat * Math.PI / 180;
  const dLat = (customerLoc.lat - providerLoc.lat) * Math.PI / 180;
  const dLng = (customerLoc.lng - providerLoc.lng) * Math.PI / 180;
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Assume average speed of 30 mph in urban areas
  const avgSpeed = 30;
  const minutes = Math.round((distance / avgSpeed) * 60);
  
  return {
    distance: distance.toFixed(1),
    minutes: Math.max(1, minutes),
    text: minutes < 1 ? 'Less than a minute' : 
          minutes === 1 ? '1 minute' : 
          `${minutes} minutes`
  };
}

/**
 * LiveTrackingMap - Real-time map showing provider location moving towards customer
 */
export default function LiveTrackingMap({ 
  customerLocation, 
  providerLocation, 
  providerName,
  providerPhone,
  serviceStatus,
  onRefresh,
  className = ''
}) {
  const [eta, setEta] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Update ETA when provider location changes
  useEffect(() => {
    if (providerLocation && customerLocation) {
      const newEta = calculateETA(providerLocation, customerLocation);
      setEta(newEta);
      setLastUpdate(new Date());
    }
  }, [providerLocation, customerLocation]);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    if (onRefresh) await onRefresh();
    setTimeout(() => setIsRefreshing(false), 500);
  };
  
  const getStatusMessage = () => {
    switch (serviceStatus) {
      case 'accepted': return 'Provider is preparing to depart';
      case 'en_route': return 'Provider is on the way to you';
      case 'arrived': return 'Provider has arrived!';
      case 'in_progress': return 'Service in progress';
      default: return 'Waiting for provider';
    }
  };
  
  const getStatusColor = () => {
    switch (serviceStatus) {
      case 'en_route': return 'bg-blue-500';
      case 'arrived': return 'bg-green-500';
      case 'in_progress': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };
  
  if (!customerLocation?.lat) {
    return (
      <div className="h-[300px] md:h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Location not available</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className}`}>
      {/* Map Container */}
      <div className="h-[300px] md:h-[400px] rounded-lg overflow-hidden">
        <MapContainer
          center={[customerLocation.lat, customerLocation.lng]}
          zoom={14}
          className="h-full w-full"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Customer Location */}
          <Marker 
            position={[customerLocation.lat, customerLocation.lng]} 
            icon={customerIcon}
          >
            <Popup>
              <div className="text-center">
                <p className="font-semibold">Your Location</p>
                <p className="text-xs text-gray-500">{customerLocation.address || 'Current position'}</p>
              </div>
            </Popup>
          </Marker>
          
          {/* Provider Location with Animation */}
          {providerLocation && (
            <>
              <AnimatedMarker 
                position={providerLocation}
                icon={providerIcon}
              >
                <Popup>
                  <div className="text-center">
                    <p className="font-semibold">{providerName || 'Your Provider'}</p>
                    {eta && <p className="text-xs text-blue-600">{eta.text} away</p>}
                  </div>
                </Popup>
              </AnimatedMarker>
              
              {/* Route Line */}
              <Polyline
                positions={[
                  [providerLocation.lat, providerLocation.lng],
                  [customerLocation.lat, customerLocation.lng]
                ]}
                pathOptions={{
                  color: '#2563EB',
                  weight: 4,
                  opacity: 0.8,
                  dashArray: '10, 10',
                  lineCap: 'round'
                }}
              />
              
              {/* Auto-fit bounds */}
              <MapBoundsUpdater 
                providerLocation={providerLocation}
                customerLocation={customerLocation}
              />
            </>
          )}
        </MapContainer>
      </div>
      
      {/* Status Overlay - Top */}
      <div className="absolute top-3 left-3 right-3 z-[1000]">
        <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor()} animate-pulse`} />
              <span className="font-medium text-sm">{getStatusMessage()}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>
      
      {/* ETA Card - Bottom */}
      {providerLocation && eta && serviceStatus === 'en_route' && (
        <div className="absolute bottom-3 left-3 right-3 z-[1000]">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{eta.minutes} min</p>
                  <p className="text-sm text-gray-500">{eta.distance} miles away</p>
                </div>
              </div>
              
              {/* Call Button */}
              {providerPhone && (
                <a 
                  href={`tel:${providerPhone}`}
                  className="w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                >
                  <Phone className="w-5 h-5 text-white" />
                </a>
              )}
            </div>
            
            {/* Last Update */}
            {lastUpdate && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Updated {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Arrived Badge */}
      {serviceStatus === 'arrived' && (
        <div className="absolute bottom-3 left-3 right-3 z-[1000]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Car className="w-5 h-5" />
              </div>
              <span className="text-lg font-bold">Provider Has Arrived!</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom CSS for animations */}
      <style jsx global>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 4px 20px rgba(37, 99, 235, 0.6); }
        }
        .provider-marker > div {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/**
 * MobileLiveTracking - Optimized mobile view with larger touch targets
 */
export function MobileLiveTracking({
  customerLocation,
  providerLocation,
  providerName,
  providerPhone,
  providerPicture,
  serviceStatus,
  eta,
  onCall,
  onMessage,
  onRefresh
}) {
  return (
    <div className="space-y-4">
      {/* Map */}
      <LiveTrackingMap
        customerLocation={customerLocation}
        providerLocation={providerLocation}
        providerName={providerName}
        providerPhone={providerPhone}
        serviceStatus={serviceStatus}
        onRefresh={onRefresh}
        className="rounded-xl overflow-hidden shadow-lg"
      />
      
      {/* Provider Card - Large Touch Targets */}
      {providerName && (
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {/* Provider Avatar */}
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {providerPicture ? (
                  <img src={providerPicture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg text-gray-900 truncate">{providerName}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {serviceStatus === 'en_route' && eta && (
                    <>
                      <Clock className="w-4 h-4" />
                      <span>{eta.minutes} min away</span>
                    </>
                  )}
                  {serviceStatus === 'arrived' && (
                    <Badge className="bg-green-500">Arrived</Badge>
                  )}
                  {serviceStatus === 'in_progress' && (
                    <Badge className="bg-orange-500">Working</Badge>
                  )}
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Large Touch Targets */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button
                variant="outline"
                className="h-14 text-base"
                onClick={onCall}
              >
                <Phone className="w-5 h-5 mr-2" />
                Call
              </Button>
              <Button
                className="h-14 text-base bg-blue-600 hover:bg-blue-700"
                onClick={onMessage}
              >
                <AlertCircle className="w-5 h-5 mr-2" />
                Message
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
